/**
 * CollaboratorActivate.jsx — Sprint 2 Iter241
 * ============================================
 * Landing d'activation de compte collaborateur depuis l'email d'invitation.
 * URL : /onboarding/collab?token=...
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Sparkles, CheckCircle2 } from 'lucide-react';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();

export default function CollaboratorActivate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/onboarding/collab/check?token=${encodeURIComponent(token)}`)
      .then(r => setInfo(r.data))
      .catch(() => setInfo({ valid: false }))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error('Mot de passe ≥ 8 caractères');
    if (password !== confirm) return toast.error('Les mots de passe ne correspondent pas');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/onboarding/collab/activate`, { token, password });
      localStorage.setItem('token', r.data.access_token);
      toast.success('Compte activé — bienvenue !');
      navigate('/collaborator/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Activation impossible');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
    </div>;
  }

  if (!info?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="max-w-md bg-white rounded-2xl shadow-sm p-8 text-center">
          <div className="text-red-600 font-medium mb-2">Lien invalide ou expiré</div>
          <p className="text-sm text-stone-500">Contactez votre référent entreprise pour recevoir un nouveau lien.</p>
        </div>
      </div>
    );
  }

  if (info.already_activated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="max-w-md bg-white rounded-2xl shadow-sm p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
          <div className="font-display text-lg">Compte déjà activé</div>
          <p className="text-sm text-stone-500 mt-1">Connectez-vous avec <strong>{info.email}</strong>.</p>
          <button onClick={() => navigate('/login')} data-testid="collab-login-redirect"
                  className="mt-4 px-4 py-2 bg-[#111827] text-white rounded-lg text-sm">
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6" data-testid="collab-activate">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="bg-[#0F0F10] text-white p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-1">
            {info.company_name} vous invite
          </div>
          <h1 className="font-display text-2xl">Bienvenue {info.first_name || ''}</h1>
          <p className="text-xs text-stone-300 mt-1">
            {info.sessions_allocated} session{info.sessions_allocated > 1 ? 's' : ''} de coaching
            {info.insights_included && ' · test COACHDIA Prism offert'}
          </p>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-[10px] tracking-wider uppercase text-stone-500">Email</label>
            <div className="text-sm font-mono text-stone-700">{info.email}</div>
          </div>
          <div>
            <label className="text-[10px] tracking-wider uppercase text-stone-500 block mb-1">Mot de passe</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                   data-testid="collab-password"
                   className="w-full border border-stone-200 px-3 py-2 text-sm rounded-lg focus:outline-none focus:border-[#92710A]"
                   minLength={8} required />
          </div>
          <div>
            <label className="text-[10px] tracking-wider uppercase text-stone-500 block mb-1">Confirmer</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                   data-testid="collab-password-confirm"
                   className="w-full border border-stone-200 px-3 py-2 text-sm rounded-lg focus:outline-none focus:border-[#92710A]"
                   minLength={8} required />
          </div>
          <button type="submit" disabled={busy} data-testid="collab-activate-submit"
                  className="w-full bg-[#92710A] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#78560B] disabled:opacity-60 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Activer mon compte
          </button>
        </form>
      </div>
    </div>
  );
}
